import { DocumentHistory } from "@/components/DocumentHistory";
import { userColor, type CollaborationState } from "@/components/editor/provider";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, getRouteApi, useNavigate, useBlocker } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { unwrap } from "@tern/api-client";
import { Archive, ArchiveRestore, LockKeyhole, UnlockKeyhole, Download, History, Link2, MessageSquare, MoreHorizontal, Trash2 } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Comments } from "@/components/Comments";
import { PageHeader } from "@/components/AppShell";
import { CopyUidButton } from "@/components/CopyUidButton";
import { EmptyState } from "@/components/EmptyState";
import { Picker } from "@/components/Picker";
import { StateIcon } from "@/components/PropertyIcons";
import { TaskStatusIcon } from "@/components/TaskProps";
import { TernEditor, type Selection, type TernEditorHandle } from "@/components/editor/TernEditor";
import type { TernTarget } from "@/components/editor/ternLinks";
import { api, errorMessage } from "@/lib/api";
import { deleteDoc, docLinksQuery, docQuery, downloadText, updateDoc, type Doc } from "@/lib/docs";
import { instancePath } from "@/lib/instance";
import { liveIssues, liveTasks, useProject, useProjectStore, type ProjectData } from "@/lib/store";
import { toast } from "@/lib/toast";
import { ProjectLoading, useLoadProject } from "./ProjectHome";

const route = getRouteApi("/$ws/doc/$id");

export function DocPage() {
  const { ws, id } = route.useParams();
  const search = route.useSearch();
  const q = useQuery(docQuery(id));
  const projectKey = useProjectStore((s) => (q.data ? s.byId[q.data.project_id]?.project.key : undefined));
  const projectsQ = useQuery({
    queryKey: ["projects", ws],
    queryFn: async () => unwrap(await api.GET("/api/workspaces/{slug}/projects", { params: { path: { slug: ws } } })),
    enabled: !!q.data && !projectKey,
  });
  const key = projectKey ?? projectsQ.data?.find((p) => p.id === q.data?.project_id)?.key ?? "";
  useLoadProject(ws, key || "_");
  const d = useProject(ws, key);
  const { t } = useTranslation();
  if (q.isError) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState title={t("docs.notFound")} body={t("docs.notFoundBody")} actions={<Link to="/$ws/inbox" params={{ ws }} className="btn">{t("common.back")}</Link>} />
      </div>
    );
  }
  if (!q.data || !d) return key ? <ProjectLoading ws={ws} kkey={key} /> : <div className="flex flex-1 items-center justify-center text-[13px] text-muted">{t("common.loading")}</div>;
  return <DocView key={id} d={d} doc={q.data} focus={search.link ? { href: search.link, occurrence: Number(search.n ?? 0) } : null} />;
}

function DocView({ d, doc, focus }: { d: ProjectData; doc: Doc; focus: { href: string; occurrence: number } | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = !!d.project.role;
  const canEdit = canManage && !doc.locked_at && !doc.archived_at;
  const me = route.useRouteContext().me;
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const title = titleDraft ?? doc.title;
  const [status, setStatus] = useState("connecting");
  const [panel, setPanel] = useState<"links" | "history" | null>(null);
  const handle = useRef<TernEditorHandle | null>(null);
  const [collaboration, setCollaboration] = useState<CollaborationState | null>(null);
  const [editorGeneration, setEditorGeneration] = useState(0);
  const [liveMarkdown, setLiveMarkdown] = useState(doc.content);
  const [pending, setPending] = useState<{ kind: "issue" | "task" | "link" | "bulk"; sel: Selection } | null>(null);

  const save = useMutation({
    mutationFn: (body: Parameters<typeof updateDoc>[1]) => updateDoc(doc.id, body),
    onSuccess: (saved) => {
      qc.setQueryData(docQuery(doc.id).queryKey, saved);
      void qc.invalidateQueries({ queryKey: ["doc", doc.id, "links"] });
      void qc.invalidateQueries({ queryKey: ["docs", d.slug, d.project.key] });
      setTitleDraft((draft) => draft?.trim() === saved.title ? null : draft);
    },
    onError: (e) => {
      toast(errorMessage(e), { tone: "error" });
    },
  });

  const titlePending = save.isPending || title.trim() !== doc.title;
  const saveState = save.isPending || collaboration?.pending ? "saving" : save.isError || titlePending ? "dirty" : "saved";
  const onMarkdownChange = useCallback((md: string) => { setLiveMarkdown(md); }, []);
  const onCollaboration = useCallback((state: CollaborationState) => {
    setCollaboration(state);
  }, []);
  const reloading = useRef(false);
  const reloadDocument = useCallback(async () => {
    if (reloading.current) return;
    reloading.current = true;
    try {
      await qc.refetchQueries({ queryKey: ["doc", doc.id], exact: true });
      const latest = qc.getQueryData(docQuery(doc.id).queryKey);
      if (latest) { setTitleDraft(null); setLiveMarkdown(latest.content); }
      setEditorGeneration((v) => v + 1);
    } finally { reloading.current = false; }
  }, [qc, doc.id]);
  useEffect(() => {
    const reset = (event: Event) => {
      const detail = (event as CustomEvent<{ id: string; pending: number }>).detail;
      if (detail.id === doc.id && !detail.pending) void reloadDocument();
    };
    window.addEventListener("tern:doc-restored", reset);
    return () => window.removeEventListener("tern:doc-restored", reset);
  }, [doc.id, reloadDocument]);
  useBlocker({
    shouldBlockFn: () => (!!collaboration?.pending || titlePending) && !window.confirm(t("docs.leaveUnsaved")),
    enableBeforeUnload: titlePending,
  });

  const onReady = useCallback(
    (h: TernEditorHandle) => {
      handle.current = h;
      if (focus) setTimeout(() => h.scrollToTernLink(focus.href, focus.occurrence), 300);
    },
    [focus],
  );

  // Clicking a tern: link opens the target.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const target = (e as CustomEvent<TernTarget>).detail;
      if (target.kind === "issue") void navigate({ to: "/$ws/issue/$identifier", params: { ws: d.slug, identifier: target.identifier } });
      else void navigate({ to: "/$ws/task/$id", params: { ws: d.slug, id: target.id } });
    };
    window.addEventListener("tern:open-link", onOpen);
    return () => window.removeEventListener("tern:open-link", onOpen);
  }, [navigate, d.slug]);

  // ⌥I / ⌥T / ⌥L on a selection → same handlers as the bubble-menu buttons.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.metaKey || e.ctrlKey) return;
      const k = e.key.toLowerCase();
      if (k !== "i" && k !== "t" && k !== "l") return;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const btn = document.querySelector<HTMLButtonElement>(`[data-tern-action="${k}"]`);
      if (btn) {
        e.preventDefault();
        btn.click();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const docUrl = `${location.origin}${instancePath}/${d.slug}/doc/${doc.id}`;
  const quoteBlock = (text: string) => `> ${text.split("\n").join("\n> ")}\n\n— [${doc.title}](${docUrl})`;

  const createIssue = async (sel: Selection) => {
    try {
      const issue = unwrap(await api.POST("/api/workspaces/{slug}/projects/{key}/issues", { params: { path: { slug: d.slug, key: d.project.key } }, body: { title: sel.text.split("\n")[0]!.slice(0, 120), description: quoteBlock(sel.text) } }));
      useProjectStore.getState().applyIssue(issue);
      handle.current?.linkRange(sel.from, sel.to, `tern:issue/${d.project.key}-${issue.number}`);
      toast(t("issue.created", { id: `${d.project.key}-${issue.number}` }), { action: { label: t("common.open"), run: () => void navigate({ to: "/$ws/issue/$identifier", params: { ws: d.slug, identifier: `${d.project.key}-${issue.number}` } }) } });
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };
  const createTask = async (sel: Selection) => {
    try {
      const task = unwrap(await api.POST("/api/workspaces/{slug}/projects/{key}/tasks", { params: { path: { slug: d.slug, key: d.project.key } }, body: { name: sel.text.split("\n")[0]!.slice(0, 200), description: quoteBlock(sel.text) } }));
      useProjectStore.getState().applyTask(task);
      handle.current?.linkRange(sel.from, sel.to, `tern:task/${task.id}`);
      toast(t("task.created", { name: task.name }), { action: { label: t("common.open"), run: () => void navigate({ to: "/$ws/task/$id", params: { ws: d.slug, id: task.id } }) } });
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    }
  };
  const bulkCreate = async (sel: Selection) => {
    let n = 0;
    // Link ranges from the end so earlier positions stay valid.
    for (const item of [...sel.listItems].reverse()) {
      try {
        const issue = unwrap(await api.POST("/api/workspaces/{slug}/projects/{key}/issues", { params: { path: { slug: d.slug, key: d.project.key } }, body: { title: item.text.slice(0, 120), description: quoteBlock(item.text) } }));
        useProjectStore.getState().applyIssue(issue);
        handle.current?.linkRange(item.from, item.to, `tern:issue/${d.project.key}-${issue.number}`);
        n += 1;
      } catch (e) {
        toast(errorMessage(e), { tone: "error" });
      }
    }
    toast(t("docs.bulkCreated", { n }));
  };

  const del = useMutation({
    mutationFn: () => deleteDoc(doc.id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["docs", d.slug, d.project.key] });
      await navigate({ to: "/$ws/project/$key/docs", params: { ws: d.slug, key: d.project.key } });
    },
    onError: (e) => toast(errorMessage(e), { tone: "error" }),
  });

  return (
    <>
      <PageHeader
        besideCrumbs={<CopyUidButton uid={doc.id} />}
        crumbs={[
          <Link key="p" to="/$ws/project/$key/docs" params={{ ws: d.slug, key: d.project.key }} className="hover:text-fg">{d.project.name} · {t("nav.docs")}</Link>,
          <span key="t">{doc.title}</span>,
        ]}
        right={
          <>
            <div className="mr-2 flex -space-x-1.5" aria-label={t("docs.participants")}>
              {collaboration?.participants.map((p) => <div key={p.id} className="relative flex h-7 w-7 items-center justify-center rounded-full border-2 border-surface text-[11px] font-semibold text-white" style={{ backgroundColor: p.color }} title={`${p.name} · ${t(p.editing ? "docs.editing" : "docs.reading")}`} aria-label={`${p.name} · ${t(p.editing ? "docs.editing" : "docs.reading")}`}>{p.name.slice(0, 1)}{p.editing && <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-surface bg-success" />}</div>)}
            </div>
            <span className="mr-2 text-[11.5px] text-faint" title={status}>
              {saveState === "saving" ? t("docs.saving") : saveState === "dirty" ? t("docs.unsaved") : t("docs.saved")} · {t(`docs.conn.${status}`)}
            </span>
            {canManage && <button className={`btn btn-ghost h-7 w-7 justify-center p-0 ${doc.locked_at ? "text-accent" : ""}`} disabled={save.isPending || (!doc.locked_at && (!!collaboration?.pending || titlePending))} title={t(doc.locked_at ? "docs.unlock" : "docs.lock")} aria-label={t(doc.locked_at ? "docs.unlock" : "docs.lock")} onClick={() => save.mutate({ locked: !doc.locked_at })}>{doc.locked_at ? <LockKeyhole className="h-4 w-4" /> : <UnlockKeyhole className="h-4 w-4" />}</button>}
            <button className="btn btn-ghost h-7 w-7 justify-center p-0" title={t("comments.title")} aria-label={t("comments.title")} onClick={() => document.getElementById(`comments-doc-${doc.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}><MessageSquare className="h-4 w-4" /></button>
            <button className={`btn btn-ghost h-7 w-7 justify-center p-0 ${panel === "links" ? "text-accent" : ""}`} title={t("docs.links")} onClick={() => setPanel((p) => (p === "links" ? null : "links"))}><Link2 className="h-4 w-4" /></button>
            <button className={`btn btn-ghost h-7 w-7 justify-center p-0 ${panel === "history" ? "text-accent" : ""}`} title={t("docs.history")} onClick={() => setPanel((p) => (p === "history" ? null : "history"))}><History className="h-4 w-4" /></button>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild><button aria-label={t("docs.actions")} className="btn btn-ghost h-7 w-7 justify-center p-0"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={4} className="z-50 w-[220px] rounded-lg border border-border bg-elevated p-1 shadow-xl">
                  <DropdownMenu.Item className="flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-[13px] outline-none data-[highlighted]:bg-hover" onSelect={() => downloadText(`${doc.title}.md`, handle.current?.getMarkdown() ?? doc.content)}><Download className="h-3.5 w-3.5" /> {t("docs.export")}</DropdownMenu.Item>
                  <DropdownMenu.Item className="flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-[13px] outline-none data-[highlighted]:bg-hover" onSelect={() => setPanel("history")}><History className="h-3.5 w-3.5" /> {t("docs.history")}</DropdownMenu.Item>
                  {canManage && <DropdownMenu.Item disabled={save.isPending || !!collaboration?.pending || titlePending} className="flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-[13px] outline-none data-[highlighted]:bg-hover data-[disabled]:opacity-40" onSelect={() => save.mutate({ archived: !doc.archived_at })}>{doc.archived_at ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}{t(doc.archived_at ? "docs.unarchive" : "docs.archive")}</DropdownMenu.Item>}
                  {canManage && <DropdownMenu.Item className="flex h-8 cursor-default items-center gap-2 rounded-md px-2 text-[13px] text-danger outline-none data-[highlighted]:bg-hover" onSelect={() => del.mutate()}><Trash2 className="h-3.5 w-3.5" /> {t("common.delete")}</DropdownMenu.Item>}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </>
        }
      />
      {(doc.locked_at || doc.archived_at) && <div className="flex shrink-0 items-center gap-2 border-b border-border bg-hover/40 px-6 py-2 text-[12px] text-muted">
        {doc.archived_at ? <Archive className="h-3.5 w-3.5" /> : <LockKeyhole className="h-3.5 w-3.5" />}{t(doc.archived_at ? "docs.archivedNotice" : "docs.lockedNotice")}
        {doc.archived_at && canManage && <button className="ml-auto text-accent hover:underline" disabled={save.isPending} onClick={() => save.mutate({ archived: false })}>{t("docs.unarchive")}</button>}
      </div>}

      {collaboration?.problem && <div role="alert" className="flex items-center gap-3 border-b border-border bg-elevated px-8 py-3 text-[12px]"><p className="flex-1 text-muted">{t("docs.collaborationProblem")}</p><button className="btn h-7 text-[12px]" onClick={() => downloadText(`${title}.md`, handle.current?.getMarkdown() ?? liveMarkdown)}>{t("docs.export")}</button><button className="btn h-7 text-[12px]" onClick={() => { if (!collaboration.pending || window.confirm(t("docs.reopenConfirm"))) void reloadDocument(); }}>{t("docs.reopen")}</button></div>}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[800px] px-10 py-8">
            <input
              className="mb-4 w-full bg-transparent text-[28px] font-bold text-fg outline-none placeholder:text-faint"
              value={title}
              readOnly={!canEdit}
              placeholder={t("docs.untitled")}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => canEdit && title.trim() && title.trim() !== doc.title && save.mutate({ title: title.trim() })}
              onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
            />
            <TernEditor
              key={editorGeneration}
              d={d}
              docId={doc.id}
              editable={canEdit}
              user={{ id: me.user.id, name: me.user.name, color: userColor(me.user.id) }}
              onMarkdownChange={onMarkdownChange}
              onCreateIssue={(sel) => void createIssue(sel)}
              onCreateTask={(sel) => void createTask(sel)}
              onLinkExisting={(sel) => setPending({ kind: "link", sel })}
              onBulkCreate={(sel) => void bulkCreate(sel)}
              onReady={onReady}
              onStatus={setStatus}
              onCollaboration={onCollaboration}
            />
            <Comments d={d} entityType="doc" entityId={doc.id} />
          </div>
        </div>
        {panel === "links" && <LinksPanel d={d} docId={doc.id} onClose={() => setPanel(null)} onJump={(href, n) => handle.current?.scrollToTernLink(href, n)} />}
        {panel === "history" && <DocumentHistory d={d} readOnly={!canEdit} docId={doc.id} title={title} content={liveMarkdown} pending={!!collaboration?.pending} onClose={() => setPanel(null)} onRestored={() => void reloadDocument()} />}
      </div>
      {pending?.kind === "link" && (
        <LinkExistingDialog
          d={d}
          onClose={() => setPending(null)}
          onPick={(href) => {
            handle.current?.linkRange(pending.sel.from, pending.sel.to, href);
            setPending(null);
          }}
        />
      )}
    </>
  );
}

function LinkExistingDialog({ d, onClose, onPick }: { d: ProjectData; onClose: () => void; onPick: (href: string) => void }) {
  const { t } = useTranslation();
  const items = [
    ...liveIssues(d).map((i) => ({ id: `tern:issue/${d.project.key}-${i.number}`, label: `${d.project.key}-${i.number} ${i.title}`, icon: <StateIcon state={d.states[i.state_id]} className="h-3 w-3" />, keywords: "issue" })),
    ...liveTasks(d).map((tk) => ({ id: `tern:task/${tk.id}`, label: tk.name, icon: <TaskStatusIcon status={tk.status} className="h-3 w-3" />, hint: t("issue.task") })),
  ];
  return (
    <Picker open onOpenChange={(o) => !o && onClose()} value={null} items={items} placeholder={t("docs.linkExistingPlaceholder")} onSelect={onPick} trigger={<span className="fixed left-1/2 top-1/3 h-px w-px" />} align="center" />
  );
}

function LinksPanel({ d, docId, onClose, onJump }: { d: ProjectData; docId: string; onClose: () => void; onJump: (href: string, n: number) => void }) {
  const { t } = useTranslation();
  const q = useQuery(docLinksQuery(docId));
  return (
    <aside className="w-[320px] shrink-0 overflow-y-auto border-l border-border p-4">
      <div className="mb-3 flex items-center justify-between text-[13px] font-semibold text-muted"><span>{t("docs.links")} · {q.data?.length ?? 0}</span><button className="text-faint hover:text-fg" onClick={onClose}>✕</button></div>
      <ul className="flex flex-col gap-2">
        {(q.data ?? []).map((l) => {
          const issue = l.target_type === "issue" ? d.issues[l.target_id] : undefined;
          const task = l.target_type === "task" ? d.tasks[l.target_id] : undefined;
          const href = issue ? `tern:issue/${d.project.key}-${issue.number}` : `tern:task/${l.target_id}`;
          return (
            <li key={l.id} className="rounded-lg border border-border p-2 text-[12.5px]">
              <button className="flex w-full items-center gap-1.5 text-left hover:underline" onClick={() => onJump(href, l.occurrence)}>
                {issue ? <StateIcon state={d.states[issue.state_id]} className="h-3 w-3" /> : task ? <TaskStatusIcon status={task.status} className="h-3 w-3" /> : null}
                <span className="truncate font-medium">{issue ? `${d.project.key}-${issue.number} ${issue.title}` : task?.name ?? "?"}</span>
              </button>
              <div className="mt-1 truncate text-faint">“{l.quote}”</div>
              {issue && <Link to="/$ws/issue/$identifier" params={{ ws: d.slug, identifier: `${d.project.key}-${issue.number}` }} className="text-[11.5px] text-accent hover:underline">{t("common.open")} →</Link>}
              {task && <Link to="/$ws/task/$id" params={{ ws: d.slug, id: task.id }} className="text-[11.5px] text-accent hover:underline">{t("common.open")} →</Link>}
            </li>
          );
        })}
        {q.data?.length === 0 && <li className="text-[12.5px] text-faint">{t("docs.noLinks")}</li>}
      </ul>
    </aside>
  );
}
